// =====================================================
// SIGNING ENVELOPES API + PUBLIC SIGNING CEREMONY API + LEASE GENERATION API + UNIT DISCLOSURE API
// =====================================================

import { z } from 'zod';
import { apiFetch, ApiClientError } from './api.core';
import {
  ManagerSigningTokenResponseSchema,
  UnitDisclosureRecordSchema,
  UnitDisclosureListItemSchema,
  ListDisclosuresResponseSchema,
  DisclosureReadinessItemSchema,
  DisclosureReadinessResponseSchema,
} from '@resida/api-types/lease-support';
import type { ResendResponse } from '@resida/api-types/lease-generation';
import type {
  JurisdictionResponse,
  PreviewRequest,
  PreviewResponse,
  GenerateRequest,
  LeaseDocumentDetailResponse,
  LeaseDocumentListResponse,
  LeaseDocumentResponse,
  UpdateDraftRequest,
  LeaseDefaults,
  DisclosureFieldsResponse,
  AssembledContentSnapshot,
  ValidateAnchorsResponse,
} from '../types/lease-generation';

// =====================================================
// SIGNING ENVELOPES API (Landlord)
// =====================================================

export function listSigningEnvelopes(params?: {
  status?: string;
  sourceType?: string;
  sourceId?: string;
  awaitingMySignature?: boolean;
}): Promise<unknown> {
  const query = new URLSearchParams();
  if (params?.status) query.set('status', params.status);
  if (params?.sourceType) query.set('sourceType', params.sourceType);
  if (params?.sourceId) query.set('sourceId', params.sourceId);
  if (params?.awaitingMySignature !== undefined) {
    query.set('awaitingMySignature', String(params.awaitingMySignature));
  }
  const qs = query.toString();
  return apiFetch<unknown>(`/landlord/api/signing/envelopes${qs ? `?${qs}` : ''}`);
}

export function getSigningEnvelope(id: string): Promise<unknown> {
  return apiFetch<unknown>(`/landlord/api/signing/envelopes/${id}`);
}

// Derived from ManagerSigningTokenResponseSchema in @resida/api-types/lease-support
export type ManagerSigningTokenResponse = z.infer<typeof ManagerSigningTokenResponseSchema>;

/**
 * Get a signing token for the authenticated manager on this envelope, so they can
 * complete their own recipient's signing ceremony inline (Landlord in-app signing).
 * Response shape re-exported verbatim from
 * apps/api/src/routes/manager/signing-envelope.schemas.ts.
 */
export function getManagerSigningToken(id: string): Promise<ManagerSigningTokenResponse> {
  return apiFetch<ManagerSigningTokenResponse>(
    `/landlord/api/signing/envelopes/${id}/signing-token`
  );
}

export function sendEnvelope(id: string): Promise<unknown> {
  return apiFetch<unknown>(`/landlord/api/signing/envelopes/${id}/send`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function cancelEnvelope(id: string): Promise<unknown> {
  return apiFetch<unknown>(`/landlord/api/signing/envelopes/${id}/cancel`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function resendToRecipient(id: string, recipientId: string): Promise<unknown> {
  return apiFetch<unknown>(`/landlord/api/signing/envelopes/${id}/resend/${recipientId}`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}


export function downloadEnvelopeDocument(id: string): Promise<Response> {
  return fetch(`/landlord/api/signing/envelopes/${id}/document`, { credentials: 'include' });
}

// =====================================================
// PUBLIC SIGNING CEREMONY API (token-authenticated, no session)
// =====================================================

interface CeremonyDocumentResponse {
  pdfBase64: string;
  fieldPlacements: Record<string, unknown[]>;
  recipientId: string;
  envelopeTitle: string;
}

interface SubmitSigningFieldsBody {
  fields: Array<{
    type: string;
    page: number;
    x: number;
    y: number;
    width: number;
    height: number;
    value: string;
  }>;
}

export function getCeremonyDocument(token: string): Promise<CeremonyDocumentResponse> {
  return apiFetch<CeremonyDocumentResponse>(`/api/signing/ceremony/${token}/document`);
}

export function submitCeremonyFields(
  token: string,
  body: SubmitSigningFieldsBody
): Promise<{ success: true }> {
  return apiFetch<{ success: true }>(
    `/api/signing/ceremony/${token}/submit`,
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export function recordCeremonyConsent(token: string): Promise<{ success: true }> {
  return apiFetch<{ success: true }>(
    `/api/signing/ceremony/${token}/consent`,
    { method: 'POST', body: JSON.stringify({ accepted: true }) }
  );
}

// =====================================================
// LEASE GENERATION API
// =====================================================

/**
 * Resolve jurisdiction rules for a unit
 */
export async function resolveJurisdiction(unitId: string): Promise<JurisdictionResponse> {
  return apiFetch<JurisdictionResponse>(
    `/landlord/api/lease-generation/jurisdiction?unitId=${encodeURIComponent(unitId)}`
  );
}

/**
 * Get saved lease defaults for the property owning this unit
 */
export async function getLeaseDefaults(unitId: string): Promise<{ defaults: LeaseDefaults | null }> {
  return apiFetch<{ defaults: LeaseDefaults | null }>(
    `/landlord/api/lease-generation/defaults?unitId=${encodeURIComponent(unitId)}`
  );
}

/**
 * Preview assembled lease content without persisting
 */
export async function previewLease(params: PreviewRequest): Promise<PreviewResponse> {
  return apiFetch<PreviewResponse>('/landlord/api/lease-generation/preview', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * Generate a lease document
 */
export async function generateLease(params: GenerateRequest): Promise<LeaseDocumentDetailResponse> {
  return apiFetch<LeaseDocumentDetailResponse>('/landlord/api/lease-generation/generate', {
    method: 'POST',
    body: JSON.stringify(params),
  });
}

/**
 * List all lease generation documents for a lease
 */
export async function listLeaseDocuments(leaseId: string): Promise<LeaseDocumentListResponse> {
  return apiFetch<LeaseDocumentListResponse>(`/landlord/api/leases/${leaseId}/lease-documents`);
}

/**
 * Void a lease generation document
 */
export async function voidLeaseDocument(
  leaseId: string,
  documentId: string
): Promise<LeaseDocumentResponse> {
  return apiFetch<LeaseDocumentResponse>(
    `/landlord/api/leases/${leaseId}/lease-documents/${documentId}/void`,
    { method: 'POST' }
  );
}

/**
 * Re-edit a generated document atomically (BR-28): voids it and returns the new DRAFT, which
 * already holds the prior edits, variables and wizard step.
 */
export async function reEditLeaseDocument(
  leaseId: string,
  documentId: string
): Promise<LeaseDocumentResponse> {
  return apiFetch<LeaseDocumentResponse>(
    `/landlord/api/leases/${leaseId}/lease-documents/${documentId}/re-edit`,
    { method: 'POST' }
  );
}

/**
 * Resend the signing invitation to one recipient (BR-26). `recipientId` is the envelope
 * SigningRecipient id. A failed email delivery rejects with a 502 ApiClientError.
 */
export async function resendDocumentInvitation(
  leaseId: string,
  documentId: string,
  recipientId: string
): Promise<ResendResponse> {
  return apiFetch<ResendResponse>(
    `/landlord/api/leases/${leaseId}/lease-documents/${documentId}/recipients/${recipientId}/resend`,
    { method: 'POST' }
  );
}

/**
 * Get the URL to download the original PDF for a lease document.
 * Returns a URL that can be used as an href (opens in browser or downloads).
 */
export function leaseDocumentPdfUrl(leaseId: string, documentId: string): string {
  return `/landlord/api/leases/${leaseId}/lease-documents/${documentId}/pdf`;
}

/**
 * Send a lease generation document for signing
 */
export async function sendLeaseDocumentForSigning(
  leaseId: string,
  documentId: string
): Promise<LeaseDocumentResponse> {
  return apiFetch<LeaseDocumentResponse>(
    `/landlord/api/leases/${leaseId}/lease-documents/${documentId}/send`,
    { method: 'POST' }
  );
}

/**
 * Get the current draft document for a unit (returns null if none exists)
 */
export async function getLeaseWizardDraft(unitId: string): Promise<LeaseDocumentDetailResponse | null> {
  try {
    return await apiFetch<LeaseDocumentDetailResponse>(
      `/landlord/api/lease-generation/draft?unitId=${encodeURIComponent(unitId)}`
    );
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) {
      return null;
    }
    throw err;
  }
}

/**
 * Create a lease wizard draft. `leaseId` is the existing lease the draft is a new document for
 * (the wizard opened with `?leaseId=`); omit it for a draft of a new lease.
 */
export async function createLeaseWizardDraft(unitId: string, leaseId?: string): Promise<LeaseDocumentResponse> {
  return apiFetch<LeaseDocumentResponse>(
    '/landlord/api/lease-generation/draft',
    {
      method: 'POST',
      body: JSON.stringify(leaseId ? { unitId, leaseId } : { unitId }),
    }
  );
}

/**
 * Save/update a wizard draft document
 */
export async function saveLeaseWizardDraft(
  documentId: string,
  data: UpdateDraftRequest
): Promise<LeaseDocumentResponse> {
  return apiFetch<LeaseDocumentResponse>(
    `/landlord/api/lease-generation/draft/${documentId}`,
    {
      method: 'PUT',
      body: JSON.stringify(data),
    }
  );
}

/**
 * Validate that all required signature anchor tokens are present in edited content
 */
export async function validateLeaseAnchors(
  unitId: string,
  editedContent: AssembledContentSnapshot,
): Promise<ValidateAnchorsResponse> {
  return apiFetch<ValidateAnchorsResponse>('/landlord/api/lease-generation/validate-anchors', {
    method: 'POST',
    body: JSON.stringify({ unitId, editedContent }),
  });
}

/**
 * Fetch field metadata for a disclosure (used by DisclosureFieldsForm)
 */
export async function fetchDisclosureFields(
  disclosureId: string,
  unitId: string
): Promise<DisclosureFieldsResponse> {
  return apiFetch<DisclosureFieldsResponse>(
    `/landlord/api/lease-generation/disclosures/${encodeURIComponent(disclosureId)}/fields?unitId=${encodeURIComponent(unitId)}`
  );
}

// =====================================================
// UNIT DISCLOSURE API
// =====================================================

// Derived from UnitDisclosureRecordSchema in @resida/api-types/lease-support
export type UnitDisclosureRecord = z.infer<typeof UnitDisclosureRecordSchema>;

// Derived from UnitDisclosureListItemSchema in @resida/api-types/lease-support
export type UnitDisclosureListItem = z.infer<typeof UnitDisclosureListItemSchema>;

// Derived from ListDisclosuresResponseSchema in @resida/api-types/lease-support
export type ListDisclosuresResponse = z.infer<typeof ListDisclosuresResponseSchema>;

export interface GetDisclosureResponse {
  disclosure: UnitDisclosureRecord | null;
}

export interface UpsertDisclosureResponse {
  disclosure: UnitDisclosureRecord;
}

// Derived from DisclosureReadinessItemSchema in @resida/api-types/lease-support
export type DisclosureReadinessItem = z.infer<typeof DisclosureReadinessItemSchema>;

// Derived from DisclosureReadinessResponseSchema in @resida/api-types/lease-support
export type DisclosureReadinessResponse = z.infer<typeof DisclosureReadinessResponseSchema>;

/**
 * List all disclosure records for a unit, including jurisdiction-required context.
 */
export async function listUnitDisclosures(unitId: string): Promise<ListDisclosuresResponse> {
  return apiFetch<ListDisclosuresResponse>(`/landlord/api/units/${unitId}/disclosures`);
}

/**
 * Get a specific disclosure record for a unit. Returns { disclosure: null } if none exists yet.
 */
export async function getUnitDisclosure(
  unitId: string,
  disclosureId: string
): Promise<GetDisclosureResponse> {
  return apiFetch<GetDisclosureResponse>(
    `/landlord/api/units/${unitId}/disclosures/${encodeURIComponent(disclosureId)}`
  );
}

/**
 * Upsert a disclosure record (create or update).
 */
export async function upsertUnitDisclosure(
  unitId: string,
  disclosureId: string,
  data: Record<string, unknown>,
  complete: boolean
): Promise<UpsertDisclosureResponse> {
  return apiFetch<UpsertDisclosureResponse>(
    `/landlord/api/units/${unitId}/disclosures/${encodeURIComponent(disclosureId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({ data, complete }),
    }
  );
}

/**
 * Mark a disclosure as reviewed (updates lastReviewedAt).
 */
export async function markDisclosureReviewed(
  unitId: string,
  disclosureId: string
): Promise<UpsertDisclosureResponse> {
  return apiFetch<UpsertDisclosureResponse>(
    `/landlord/api/units/${unitId}/disclosures/${encodeURIComponent(disclosureId)}/review`,
    { method: 'POST' }
  );
}

/**
 * Check disclosure readiness for a unit (for lease generation gating).
 */
export async function checkDisclosureReadiness(
  unitId: string
): Promise<DisclosureReadinessResponse> {
  return apiFetch<DisclosureReadinessResponse>(
    `/landlord/api/units/${unitId}/disclosures/readiness`
  );
}
